import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/App";
import { 
  Castle, 
  ChevronLeft, 
  LogOut,
  LayoutDashboard,
  Calendar,
  Settings,
  Users,
  Building2,
  ChevronDown,
  Shield
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Header = ({ title, showBack = false }) => {
  const navigate = useNavigate();
  const { user, organization, organizations, logout, switchOrganization } = useAuth();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [unviewedCount, setUnviewedCount] = useState(0);

  useEffect(() => {
    const checkSuperAdmin = async () => {
      try {
        const response = await fetch(`${API}/admin/check`, { credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          setIsSuperAdmin(data.is_super_admin);
        }
      } catch (error) {
        console.error(error);
      }
    };
    if (user) {
      checkSuperAdmin();
      fetchUnviewedCount();
    }
  }, [user]);

  useEffect(() => {
    // Refresh unviewed count every 30 seconds
    const interval = setInterval(fetchUnviewedCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnviewedCount = async () => {
    try {
      const response = await fetch(`${API}/reservations/unviewed-count`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUnviewedCount(data.count);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Déconnecté");
    navigate("/login", { replace: true });
  };

  const handleSwitchOrg = async (orgId) => {
    if (orgId === organization?.id) return;
    const success = await switchOrganization(orgId);
    if (success) {
      toast.success("Organisation changée");
      window.location.reload();
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-pink-100">
      <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
        <div className="flex items-center gap-3">
          {showBack ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-xl"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
          ) : (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-festive-pink to-festive-purple flex items-center justify-center shadow-md">
              <Castle className="w-5 h-5 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-xl font-heading font-bold text-gray-900">
              {title}
            </h1>
            {organization && (
              <p className="text-xs text-gray-500 -mt-0.5">{organization.name}</p>
            )}
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="rounded-xl relative"
          >
            <LayoutDashboard className="w-4 h-4 mr-2" />
            Accueil
            {unviewedCount > 0 && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/online-pending");
                }}
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-cyan-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse hover:bg-cyan-600 cursor-pointer"
              >
                {unviewedCount > 9 ? "9+" : unviewedCount}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/calendar")}
            className="rounded-xl"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Planning
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/structures")}
            className="rounded-xl"
          >
            <Castle className="w-4 h-4 mr-2" />
            Catalogue
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/team")}
            className="rounded-xl"
          >
            <Users className="w-4 h-4 mr-2" />
            Équipe
          </Button>
          <Button
            onClick={() => navigate("/reservations/new")}
            className="rounded-xl bg-gradient-to-r from-festive-pink to-festive-purple text-white hover:opacity-90"
          >
            Nouvelle réservation
          </Button>
        </nav>

        <div className="flex items-center gap-2">
          {/* Organization Switcher (if multiple) */}
          {organizations?.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="hidden md:flex rounded-xl gap-2">
                  <Building2 className="w-4 h-4" />
                  <span className="max-w-[100px] truncate">{organization?.name}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Organisations</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {organizations.map((org) => (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={() => handleSwitchOrg(org.id)}
                    className={org.id === organization?.id ? "bg-pink-50" : ""}
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    {org.name}
                    {org.id === organization?.id && (
                      <span className="ml-auto text-xs text-festive-pink">Actif</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-xl"
                data-testid="menu-btn"
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-gradient-to-br from-festive-pink to-festive-purple text-white text-xs">
                    {user?.name?.charAt(0)?.toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-gray-500 font-normal">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="md:hidden"
                onClick={() => navigate("/dashboard")}
              >
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Accueil
              </DropdownMenuItem>
              <DropdownMenuItem
                className="md:hidden"
                onClick={() => navigate("/calendar")}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Planning
              </DropdownMenuItem>
              <DropdownMenuItem
                className="md:hidden"
                onClick={() => navigate("/structures")}
              >
                <Castle className="w-4 h-4 mr-2" />
                Catalogue
              </DropdownMenuItem>
              <DropdownMenuItem
                className="md:hidden"
                onClick={() => navigate("/team")}
              >
                <Users className="w-4 h-4 mr-2" />
                Équipe
              </DropdownMenuItem>
              <DropdownMenuSeparator className="md:hidden" />
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <Settings className="w-4 h-4 mr-2" />
                Paramètres
              </DropdownMenuItem>
              {isSuperAdmin && (
                <DropdownMenuItem 
                  onClick={() => navigate("/super-admin")}
                  className="text-purple-600 focus:text-purple-600"
                  data-testid="super-admin-link"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Super Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 focus:text-red-600"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
